/* Courted · date night (the redesign; the app only). Swipe on places near home on your own; what you both say
   yes to goes in your ideas; This week suggests one for each time you're both free, and Plan it puts it on the
   week. Three sections as one segmented control (This week · Swipe · Ideas); a place is a page over them; Plan
   it and What to show are sheets. First time in: how far you'll go, and the domovoi with his map.
   Data: sync.js (date_swipes, the deck through courted-places, the catalogue's words); pure parts: helpers.js. */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { routerFor, inShell } from '../core/router.js';
import { ShellChips, useShellRetap } from '../ui/shell-chips.jsx';
import { weekFree, onWeekFree } from '../core/week-free.js';
import { sendIntent } from '../core/intent.js';
import * as H from './helpers.js';
import * as S from './sync.js';
import * as W from './weather.js';
import { Sheet, Mapper, Art, Ico, useToast, track } from './ui.jsx';
import { SwipeR, WhereBar, WhatWhereSheet } from './screen-swipe.jsx';
import { SearchR } from './screen-search.jsx';
import { IdeasR, PlaceR } from './screen-ideas.jsx';
import { WeekR } from './screen-week.jsx';
import { PlanSheet } from './sheet-plan.jsx';
import { SpotR } from './screen-spot.jsx';
import * as P from './perfect.js';
import { DaysR, NewDaySheet, DayPage, AddSheet, ItemSheet, MoreSheet, PlanDaySheet } from './screen-days.jsx';

const { useState, useEffect, useMemo, useRef, useCallback } = React;
const SECTIONS = [{ id: 'week', label: 'This week' }, { id: 'swipe', label: 'Swipe' }, { id: 'ideas', label: 'Ideas' }];
// Perfect days (Ours › Perfect days) is a section of its own, not one of the three
const secLabel = (s) => (SECTIONS.find((x) => x.id === s) || { label: 'Perfect days' }).label;
const ISO = /^\d{4}-\d{2}-\d{2}$/;
const KINDS_KEY = 'domovoi.courted.kinds.v1';
const STARTED_KEY = 'domovoi.courted.started.v1';
const WHERE_KEY = 'domovoi.courted.where.v1';   // the town you're looking at on this phone (none: near home)
const read = (k, d) => { try { const v = JSON.parse(localStorage.getItem(k) || 'null'); return v == null ? d : v; } catch (e) { return d; } };
const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} };
const todayISO = () => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };
const noSignal = () => typeof navigator !== 'undefined' && navigator.onLine === false;
// Another product's page: its tab in the app, the page itself on the web.
const openPath = (path) => { if (inShell()) window.dispatchEvent(new CustomEvent('dv-open', { detail: { path } })); else window.location.assign(path); };
const SIGN_IN = () => (inShell() ? '/signin/?next=%2Fshell.html%3Fopen%3D%2Fcourted%2F' : '/signin/?next=%2Fcourted%2F');

function parse(loc) {
  const p = (loc.pathname || '').replace(/^\/courted\/?/, '');
  const q = new URLSearchParams(loc.search || '');
  const [a, b] = p.split('/');
  // Perfect days: the list (?new=1 starts one), and one of them (?plan=<date> opens Plan it, for Home's card)
  if (a === 'days') return { section: 'days', place: null, newDay: q.get('new') === '1' };
  if (a === 'day' && b) return { section: 'days', place: null, day: decodeURIComponent(b), planFor: ISO.test(q.get('plan') || '') ? q.get('plan') : q.get('plan') ? 'next' : null };
  // ?for=: the day you came to plan it for (a free time on This week)
  if (a === 'place' && b) return { section: q.get('from') || 'ideas', place: decodeURIComponent(b), via: q.get('via') || null, placeFor: /^\d{4}-\d{2}-\d{2}$/.test(q.get('for') || '') ? q.get('for') : null, day: q.get('day') || null };
  // Go out anywhere: search near home or in a town, as a page over This week or Swipe
  if (a === 'search') return { section: ['week', 'swipe', 'ideas'].includes(q.get('from')) ? q.get('from') : 'week', place: null, search: q.get('q') || '' };
  if (a === 'spot' && /^\d{4}-\d{2}-\d{2}$/.test(q.get('date') || '')) return { section: 'week', place: null, spot: q.get('date'), from: q.get('from') || null };
  const t = q.get('tab');
  // the morning after a date: "How was it?" (the push's address, 0048)
  if (/^[0-9a-f-]{36}$/.test(q.get('how') || '')) return { section: 'week', place: null, how: q.get('how') };
  const forDate = /^\d{4}-\d{2}-\d{2}$/.test(q.get('for') || '') ? q.get('for') : null;   // Swipe for one free time
  return { section: ['week', 'swipe', 'ideas'].includes(a) ? a : ['week', 'swipe', 'ideas'].includes(t) ? t : 'week', place: null, forDate };
}

/* First time in: how far you'll go, then Start swiping. */
function Start({ ctx, onStart }) {
  const [r, setR] = useState(ctx.range || 30);
  return (
    <div className="co-main co-start">
      <Mapper w={190} />
      <h2 className="co-display">Where shall we go?</h2>
      <p className="lede">Swipe on places near {ctx.town || 'home'} on your own. What you {ctx.partner ? 'both ' : ''}say yes to goes in your ideas, and we’ll suggest one when you’re {ctx.partner ? 'both ' : ''}free.</p>
      <span className="co-ey">How far will you go?</span>
      <div className="co-ranges" role="radiogroup" aria-label="How far will you go?">
        {H.RANGES.map(([m, l]) => <button key={m} type="button" className="dv-chip" role="radio" aria-checked={r === m ? 'true' : 'false'} aria-pressed={r === m ? 'true' : 'false'} onClick={() => setR(m)}>{l}</button>)}
      </div>
      <p className="dv-note co-note">{H.rangeWords(r, ctx.town)}{ctx.partner ? ' For both of you.' : ''}</p>
      <button type="button" className="dv-btn dv-btn-pop" onClick={() => onStart(r)}>Start swiping</button>
    </div>
  );
}

function Message({ title, text, action }) {
  return <div className="co-main co-empty co-msg"><Art id="msg" kind="outdoors" /><h2 className="co-display">{title}</h2><p>{text}</p>{action || null}</div>;
}

/* Been there, on the deck: how was it? Loved it (a favourite) or Not again (for now). Leave it and it's just been. */
function After({ a, card, cat, kind, onAnswer, onClose }) {
  useEffect(() => { const id = setTimeout(onClose, 9000); return () => clearTimeout(id); }, [a.n]);
  const name = (cat && cat.name) || (card && card.name) || 'it';
  return (
    <div className="co-match co-after dv-glass" role="status" aria-live="polite">
      <span className="th"><Art id={a.id} kind={kind} type={card && card.type} /></span>
      <button type="button" className="tx" onClick={onClose}><b>How was it?</b><span>{name}</span></button>
      <button type="button" className="dv-pill dv-pill-pop" onClick={() => onAnswer('loved')}>Loved it</button>
      <button type="button" className="dv-pill dv-pill-soft" onClick={() => onAnswer('never')}>Not again</button>
    </div>
  );
}

/* How was it? The morning after a date (the push, or This week's card): Loved it, or Wouldn't go again for now. */
function HowSheet({ plan, name, card, cat, kind, today, onAnswer, onClose }) {
  const d = new Date(plan.on_date + 'T12:00:00');
  const day = plan.on_date === H.addDays(today, -1) ? (plan.start_time && plan.start_time < '17:00' ? 'Yesterday' : 'Last night') : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getDay()];
  return (
    <Sheet title="How was it?" onClose={onClose} cancel="Not now">
      <div className="co-how">
        <span className="co-how-ph"><Art id={plan.ref.place.id} kind={kind} type={card && card.type} /></span>
        <b className="co-display">{name || plan.title}</b>
        <small>{day}</small>
      </div>
      <div className="co-how-acts">
        <button type="button" className="dv-btn dv-btn-pop" onClick={() => onAnswer('loved')}><Ico n="i-heart-f" s />Loved it</button>
        <button type="button" className="dv-btn dv-btn-soft" onClick={() => onAnswer('never')}>Wouldn’t go again, for now</button>
      </div>
      <p className="dv-note co-note">A favourite comes up again when you’re both free. Wouldn’t go again stays off your ideas.</p>
    </Sheet>
  );
}

/* The match: a glass banner over the header. Swiping for a free time, any yes asks "For Friday?" with Plan it. */
function Match({ m, card, cat, kind, onPlan, onClose }) {
  useEffect(() => { const id = setTimeout(onClose, 6000); return () => clearTimeout(id); }, [m.n]);
  const name = (cat && cat.name) || (card && card.name) || 'It';
  return (
    <div className="co-match dv-glass" role="status" aria-live="polite">
      <span className="th"><Art id={m.id} kind={kind} type={card && card.type} /></span>
      <button type="button" className="tx" onClick={onClose}>{m.mine ? <><b>For {m.day}?</b><span>{name}</span></> : <><b>You both said yes</b><span>{name}, in your ideas</span></>}</button>
      <button type="button" className="dv-pill dv-pill-pop" onClick={onPlan}>Plan it</button>
    </div>
  );
}

function App() {
  const router = routerFor('courted');
  const [loc, setLoc] = useState(() => parse(router.location));
  useEffect(() => router.listen((l) => setLoc(parse(l))), []);
  const go = useCallback((path) => { router.push(path); setLoc(parse(router.location)); }, []);
  const back = useCallback(() => { if (router.canBack()) router.back(); else go('/courted/' + loc.section); }, [loc.section]);
  useShellRetap('courted', () => { if (router.reset) router.reset('/courted/'); setLoc(parse(router.location)); });

  const [ctx, setCtx] = useState(null);
  const [rows, setRows] = useState(null);
  const [cats, setCats] = useState([]);
  const [cardsById, setCards] = useState({});
  const [deckIds, setDeckIds] = useState(null);
  const [deckErr, setDeckErr] = useState(null);
  const [kinds, setKinds] = useState(() => read(KINDS_KEY, []));
  // Go out anywhere: the towns you've looked at, and the one you're looking at now (this phone)
  const [towns, setTowns] = useState([]);
  const [townId, setTownId] = useState(() => read(WHERE_KEY, null));
  const [townCats, setTownCats] = useState([]);
  // the free time you're swiping for (this session; from This week's "Swipe for Friday" or the When chip)
  const [forDate, setForDate] = useState(() => loc.forDate || null);
  useEffect(() => { if (loc.forDate) setForDate(loc.forDate); }, [loc.forDate]);
  const [sheet, setSheet] = useState(null);
  const [match, setMatch] = useState(null);
  const [planned, setPlanned] = useState([]);
  const [free, setFree] = useState(() => weekFree());
  const [last, setLast] = useState(null);
  const [theatres, setTheatres] = useState(null);
  const [fc, setFc] = useState(null);
  const [rate, setRate] = useState([]);     // dates that have been, to say how they went
  const [after, setAfter] = useState(null); // Been there on the deck: how was it?
  const [toastEl, toast] = useToast();
  const today = todayISO();
  // Perfect days (0073): the household's, with what's in each; and what's on the week for eight weeks, for when one fits
  const [pdays, setPdays] = useState(null);
  const [busyRows, setBusyRows] = useState([]);
  const [homePicks, setHomePicks] = useState(null);

  // you, home, everyone's answers, our words for the city
  const load = useCallback(async (force) => {
    const c = await S.context(force);
    setCtx(c);
    if (c.status !== 'ready') return;
    const [r, k, p, rt, tw, pd, br] = await Promise.all([S.swipes(c).catch(() => null), S.catalogue(c), S.planned(c, today, H.addDays(today, 7)).catch(() => []), S.toRate(c, today).catch(() => []), S.towns(c).catch(() => []),
      S.perfectDays(c).catch(() => null), S.plansBetween(c, today, H.addDays(today, 60)).catch(() => [])]);
    setRows(r || []); setCats(k || []); setPlanned(p || []); setRate(rt || []); setTowns(tw || []); setPdays(pd || []); setBusyRows(br || []);
  }, [today]);
  useEffect(() => { load(); }, []);
  useEffect(() => onWeekFree(() => setFree(weekFree())), []);
  useEffect(() => { const on = () => load(true); window.addEventListener('online', on); return () => window.removeEventListener('online', on); }, []);
  // your partner's answers as they happen
  useEffect(() => {
    if (!ctx || ctx.status !== 'ready') return undefined;
    let t = null, u = null;
    S.subscribe(ctx, () => { clearTimeout(t); t = setTimeout(() => S.swipes(ctx).then(setRows).catch(() => {}), 400); },
      () => { clearTimeout(u); u = setTimeout(() => S.perfectDays(ctx).then(setPdays).catch(() => {}), 500); });
    return () => { clearTimeout(t); clearTimeout(u); S.unsubscribe(); };
  }, [ctx && ctx.status, ctx && ctx.coupleId]);

  const ready = ctx && ctx.status === 'ready';
  const me = ready ? ctx.me : null, partner = ready ? ctx.partner : null;
  const words = H.words(ready ? ctx.country : 'CA');
  // the town you're looking at (a town that's gone, or isn't the household's: near home)
  const town = useMemo(() => (townId && towns.find((t) => t.id === townId)) || null, [townId, towns]);
  useEffect(() => { if (ready && town) S.catalogue(ctx, town).then(setTownCats).catch(() => setTownCats([])); else setTownCats([]); }, [ready, town && town.id]);
  const catById = useMemo(() => { const o = {}; [...cats, ...townCats].forEach((c) => { o[c.place_id] = c; }); return o; }, [cats, townCats]);
  const mineV = useMemo(() => (me ? H.verdicts(rows, me.id) : { v: {}, k: {} }), [rows, me]);
  const theirsV = useMemo(() => (partner ? H.verdicts(rows, partner.id) : { v: {}, k: {} }), [rows, partner]);
  // each place keeps its own yeses: home's ideas are the places swiped near home; a town's are its own (Ideas shows them apart)
  const homeRows = useMemo(() => H.rowsIn(rows, null), [rows]);
  const ideas = useMemo(() => (me ? H.ideasOf(homeRows, me.id, partner && partner.id) : []), [homeRows, me, partner]);
  const waiting = useMemo(() => (me ? H.waitingOf(rows, me.id, partner && partner.id) : []), [rows, me, partner]);
  // yours to suggest (your yeses your partner hasn't answered), your favourites, and where one of you wouldn't go again
  const mine = useMemo(() => (me ? H.mineOf(homeRows, me.id, partner && partner.id) : []), [homeRows, me, partner]);
  const favs = useMemo(() => (me ? H.favouritesOf(rows, me.id, partner && partner.id) : []), [rows, me, partner]);
  const notAgain = useMemo(() => (me ? H.notAgainOf(rows, me.id, partner && partner.id) : []), [rows, me, partner]);
  const away = useMemo(() => (me ? towns.map((t) => { const r = H.rowsIn(rows, t.id); return { town: t, both: H.ideasOf(r, me.id, partner && partner.id), list: [...H.ideasOf(r, me.id, partner && partner.id), ...H.mineOf(r, me.id, partner && partner.id)] }; }) : []), [towns, rows, me, partner]);
  const townLines = useMemo(() => { const o = {}; away.forEach((a) => { const n = a.both.length, m = a.list.length - n; o[a.town.id] = n ? n + (partner ? ' you both like' : ' you like') : m ? m + ' of yours' : null; }); return o; }, [away, partner]);
  // with their kind, and the months the catalogue says they're worth it
  const meta = useCallback((list) => list.map((i) => ({ ...i, kind: H.kindOf(i.id, catById[i.id], cardsById[i.id], i.kind), months: (catById[i.id] && catById[i.id].open_months) || null })), [catById, cardsById]);
  const ideasK = useMemo(() => meta(ideas), [ideas, meta]);
  const mineK = useMemo(() => meta(mine), [mine, meta]);
  const started = ready && ((read(STARTED_KEY, []) || []).includes(me.id) || Object.keys(mineV.v).length > 0);
  // the weather at home (T3): Google's forecast, once a session
  useEffect(() => { if (ready && ctx.home && !fc) S.weather().then((f) => { if (f) setFc(f); }); }, [ready, ctx && ctx.home]);
  const wx = useMemo(() => (fc ? W.byDate(fc) : null), [fc]);
  const lat = ready && ctx.home ? ctx.home.lat : null;
  const sunsetFor = useCallback((date) => (wx && wx[date] && wx[date].down != null ? wx[date].down
    : ready && ctx.home ? H.sunsetLocal(date, ctx.home.lat, ctx.home.lng, -new Date(date + 'T12:00:00').getTimezoneOffset()) : null), [ready, ctx, wx]);
  const skyFor = useCallback((date) => { const d = wx && wx[date]; return !d ? null : W.goodDay(d, lat) ? 'good' : W.wetDay(d) ? 'wet' : null; }, [wx, lat]);
  const wxNote = useCallback((date) => { const d = wx && wx[date]; if (!d) return null; const w = W.wetDay(d); return { words: W.weatherWords(d, ctx.country), wet: w ? (w === 'snow' ? 'Snow’s' : w === 'storm' ? 'A storm’s' : 'Rain’s') : null }; }, [wx, ctx]);
  // with children, each time says whether it needs a sitter (Home's `care`)
  const slots = useMemo(() => (free ? H.withCare(H.slotsFrom(free.days, today), free.kids ? free.care : null) : []), [free, today]);
  // one idea for each free time: the ones you both like first, then yours to suggest (your partner is asked)
  const picks = useMemo(() => H.ideasForSlots(slots, [...ideasK, ...mineK], sunsetFor, skyFor), [slots, ideasK, mineK, sunsetFor, skyFor]);
  const nameOfPlan = (p) => { const id = p && p.ref && p.ref.place && p.ref.place.id; return (id && catById[id] && catById[id].name) || (id && cardsById[id] && cardsById[id].name) || (p && p.title) || ''; };
  const kindOfPlan = useCallback((p) => { const id = p.ref && p.ref.place && p.ref.place.id; return (p.ref && p.ref.kind) || (id && (mineV.k[id] || theirsV.k[id])) || null; }, [mineV, theirsV]);
  const chance = useMemo(() => (fc && free && free.segs ? W.outdoorChance({ forecast: fc, segs: free.segs, today, lat, busyDates: planned.filter((p) => kindOfPlan(p) === 'outdoors').map((p) => p.on_date) }) : null), [fc, free, today, lat, planned, kindOfPlan]);
  useEffect(() => { if (chance && loc.section === 'week' && !loc.spot) track('courted_nudge', { action: 'shown', from: 'week' }); }, [chance && chance.date, loc.section]);

  // the deck: ids near home (once per range this session) or around the town you're looking at, then cards a few ahead
  const loadDeck = useCallback(() => {
    if (!ready) return;
    setDeckErr(null);
    S.deck(ctx.range, undefined, town ? town.id : null).then((j) => setDeckIds(j.ids || {})).catch((e) => setDeckErr(noSignal() ? 'offline' : e.message === 'no_home' ? 'no_home' : 'error'));
  }, [ready, ctx && ctx.range, town && town.id]);
  useEffect(() => { setDeckIds(null); setDeckErr(null); }, [town && town.id]);
  useEffect(() => { if (ready && (started || loc.spot) && !deckIds && !deckErr && (ctx.home || town)) loadDeck(); }, [ready, started, loc.spot, deckIds, deckErr, loadDeck]);
  const limit = ready && !town ? Math.round(ctx.range * 1.3 + 5) : 1e9;
  // your partner's yeses waiting go first in the deck, but only the ones made where you're looking
  const theirsHere = useMemo(() => (partner ? H.verdicts(H.rowsIn(rows, town ? town.id : null), partner.id) : { v: {}, k: {} }), [rows, town, partner]);
  // swiping for one free time: the kinds that fit it (no hikes after dark or on a wet day), in season that day
  const forSlot = useMemo(() => (forDate ? slots.find((x) => x.date === forDate) || (forDate >= today ? H.slotsFrom([{ date: forDate, from: 7 * 60, to: 23 * 60, all: true }], today)[0] || null : null) : null), [forDate, slots, today]);
  const forKinds = useMemo(() => H.kindsFor(kinds, forSlot, forSlot ? sunsetFor(forSlot.date) : null, forSlot ? skyFor(forSlot.date) : null), [kinds, forSlot, sunsetFor, skyFor]);
  const deck = useMemo(() => H.buildDeck({ ids: deckIds || {}, cats: town ? townCats : cats, mine: mineV.v, theirs: theirsHere.v, theirKinds: theirsHere.k, kinds: forKinds.kinds.length ? forKinds.kinds : ['none'] })
    .filter((x) => { const c = cardsById[x.id]; return !(c && (c.missing || (c.minutes != null && c.minutes > limit && !x.partnerYes))); })
    .filter((x) => !forSlot || H.inSeason(catById[x.id] && catById[x.id].open_months, forSlot.date)), [deckIds, cats, townCats, town, mineV, theirsHere, forKinds, cardsById, limit, forSlot, catById]);
  const want = useRef(new Set());
  // away from home, a card comes without the drive from home
  const fetchCards = useCallback((ids, away) => {
    const need = ids.filter((id) => id && !cardsById[id] && !want.current.has(id));
    if (!need.length) return;
    need.forEach((id) => want.current.add(id));
    S.cards(need, away || null).then((got) => setCards((c) => ({ ...c, ...got }))).catch(() => {}).finally(() => need.forEach((id) => want.current.delete(id)));
  }, [cardsById]);
  useEffect(() => { if (loc.section === 'swipe' && deck.length) fetchCards(deck.slice(0, 6).map((x) => x.id), town ? town.id : null); }, [loc.section, deck, fetchCards, town]);
  useEffect(() => { const ids = away.flatMap((a) => a.list.map((i) => i.id)); if (ids.length && loc.section === 'ideas') fetchCards(ids.slice(0, 40), 'away'); }, [away, loc.section, fetchCards]);
  useEffect(() => { if (ideas.length || waiting.length || mine.length || favs.length) fetchCards([...new Set([...ideas.map((i) => i.id), ...favs.map((i) => i.id), ...waiting, ...mine.map((i) => i.id)])].slice(0, 40)); }, [ideas, waiting, mine, favs, fetchCards]);
  useEffect(() => { if (rate.length) fetchCards(rate.map((p) => p.ref.place.id)); }, [rate, fetchCards]);
  useEffect(() => { if (loc.place) fetchCards([loc.place]); }, [loc.place, fetchCards]);
  useEffect(() => { if (planned.length) fetchCards(planned.map((p) => p.ref && p.ref.place && p.ref.place.id).filter(Boolean)); }, [planned, fetchCards]);
  // the places in your perfect days (in a town, without the drive from home)
  useEffect(() => {
    if (!pdays) return;
    const open = loc.day ? pdays.filter((p) => p.id === loc.day) : pdays;
    open.forEach((p) => { const ids = p.items.filter((x) => x.kind === 'place').map((x) => x.ref); if (ids.length) fetchCards(ids.slice(0, 30), p.town_id || null); });
  }, [pdays, loc.day, fetchCards]);
  useEffect(() => { if (ready) track('courted_viewed', { section: loc.place ? 'place' : loc.section }); }, [ready, loc.section, loc.place]);
  // In theatres: what's showing in your country, and which of those you said yes to in Curated
  useEffect(() => {
    if (!ready || loc.section !== 'week' || theatres) return;
    S.nowPlaying(ctx.country).then((films) => S.filmSwipes(films.map((f) => f.id)).catch(() => []).then((sw) => setTheatres(H.inTheatres(films, sw, me.id, partner && partner.id))));
  }, [ready, loc.section, theatres]);

  /* ---- answers ---- */
  const answer = useCallback(async (id, kind, verdict, from) => {
    const prev = mineV.v[id] || null;
    setLast({ id, kind, prev });
    const now = new Date().toISOString();
    // made in the town you're looking at (the deck, a search, or a place you opened from them): that town's
    const here = town && (from === 'deck' || from === 'search' || (from === 'place' && (loc.section === 'swipe' || loc.via === 'search'))) ? town.id : null;
    const was = (rows || []).find((x) => x.user_id === me.id && x.place_id === id);
    setRows((r) => [...(r || []).filter((x) => !(x.user_id === me.id && x.place_id === id)), { place_id: id, user_id: me.id, kind, verdict, town_id: here || (was && was.town_id) || null, created_at: now, updated_at: now }]);
    track('courted_swiped', { verdict, partner_yes: theirsV.v[id] === 'yes', from: from || 'deck', catalogue: !!catById[id] });
    const forDay = from === 'deck' && forSlot ? (forSlot.date === today ? 'today' : forSlot.date === H.addDays(today, 1) ? 'tomorrow' : forSlot.dow) : null;
    if (H.yesLike(verdict) && !H.yesLike(prev) && partner && H.yesLike(theirsV.v[id])) { setMatch({ id, kind, n: Date.now(), date: forSlot && from === 'deck' ? forSlot.date : null }); track('courted_matched', { kind }); }
    else if (verdict === 'yes' && forDay) setMatch({ id, kind, n: Date.now(), date: forSlot.date, day: forDay, mine: true });
    // Been there, from the deck: how was it?
    if (verdict === 'been' && from === 'deck') setAfter({ id, kind, n: Date.now() });
    if (verdict === 'loved' || verdict === 'never') { setAfter(null); toast(verdict === 'loved' ? 'In your favourites.' : 'We won’t suggest it again.'); track('courted_after', { verdict, from: from || 'deck' }); }
    try { await S.answer(ctx, id, kind, verdict, here); } catch (e) { toast(noSignal() ? 'You’re offline. Try again with a signal.' : 'Couldn’t save that. Try again.'); S.swipes(ctx).then(setRows).catch(() => {}); }
  }, [mineV, theirsV, me, partner, ctx, catById, forSlot, today, town, loc.section, loc.via, rows]);
  const undo = useCallback(async () => {
    if (!last) return;
    const { id, kind, prev } = last;
    setLast(null);
    setRows((r) => { const rest = (r || []).filter((x) => !(x.user_id === me.id && x.place_id === id)); return prev ? [...rest, { place_id: id, user_id: me.id, kind, verdict: prev, updated_at: new Date().toISOString() }] : rest; });
    track('courted_undone', {});
    try { if (prev) await S.answer(ctx, id, kind, prev); else await S.forget(ctx, id); } catch (e) { S.swipes(ctx).then(setRows).catch(() => {}); }
  }, [last, me, ctx]);

  const start = async (range) => {
    write(STARTED_KEY, [...(read(STARTED_KEY, []) || []), me.id]);
    track('courted_started', { range });
    if (range !== ctx.range) await setRange(range);
    else setCtx({ ...ctx });
    go('/courted/swipe');
  };
  const setRange = async (m) => {
    setCtx((c) => ({ ...c, range: m }));
    setDeckIds(null);
    S.forgetDeck();
    S.catalogue({ ...ctx, range: m }).then(setCats).catch(() => {});
    track('courted_range_set', { minutes: m });
    try { await S.setRange(ctx, m); } catch (e) { toast('Couldn’t save that. Try again.'); }
  };
  const setKindsKept = (k) => { setKinds(k); write(KINDS_KEY, k); track('courted_kinds_set', { n: k.length || 4 }); };
  // where you're looking: near home, or a town (kept on this phone; the town's last use for the household)
  const pickWhere = (t, from) => {
    setTownId(t ? t.id : null); write(WHERE_KEY, t ? t.id : null);
    if (t) { setTowns((list) => [{ ...t, used_at: new Date().toISOString() }, ...list.filter((x) => x.id !== t.id)]); S.useTown(t.id).catch(() => {}); }
    track('courted_where', { away: !!t, from: from || 'sheet' });
  };
  const addTown = async (pick, session) => {
    try {
      const t = await S.addTown(ctx, pick, session);
      setTowns((list) => [t, ...list.filter((x) => x.id !== t.id)]);
      pickWhere(t, 'new');
      return t;
    } catch (e) { toast(noSignal() ? 'You’re offline. Try again with a signal.' : 'Couldn’t add ' + (pick.name || 'that') + '. Try again.'); throw e; }
  };

  /* ---- Plan it ---- */
  const openPlan = (id, date, from, extra) => setSheet({ kind: 'plan', id, date, from, ...(extra || {}) });
  // a rained-off hike: plan somewhere inside on that day, at the hike's time, and take the hike off the week
  const hm = (t) => (t ? Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5)) : null);
  const openSwap = (plan, id) => openPlan(id, plan.on_date, 'rain', { replaces: plan });
  const openSpot = (date, from) => go('/courted/spot?date=' + date + '&from=' + (from || 'week'));
  // Plan it's days: your free times, and a fine day's daylight when it isn't one of them (Find a spot)
  const planSlots = useMemo(() => {
    // swapping a rained-off hike: that day is the hike's time, whatever else is free then
    const r = sheet && sheet.replaces;
    const list = r ? slots.filter((x) => x.date !== r.on_date) : slots;
    const add = [];
    if (chance && !list.some((x) => x.date === chance.date) && !(r && r.on_date === chance.date)) add.push({ date: chance.date, from: chance.from, to: chance.to, all: false });
    if (r) { const s0 = hm(r.start_time) ?? 13 * 60; add.push({ date: r.on_date, from: s0, to: Math.min(23 * 60, s0 + (r.minutes || 180)), all: false }); }
    const all = add.length ? [...list, ...H.slotsFrom(add, today)].sort((a, b) => (a.date < b.date ? -1 : 1)) : list;
    // and the day you came for (Home's date night, Swipe for Friday, a place from Friday's card, Find a spot's day),
    // even when it isn't one of your free times
    return H.withDays(all, [forDate, loc.placeFor, loc.spot], today);
  }, [slots, chance, today, sheet, forDate, loc.placeFor, loc.spot]);
  const doPlan = async (id, opts, from) => {
    const card = cardsById[id], cat = catById[id];
    const kind = H.kindOf(id, cat, card, mineV.k[id]);
    try {
      const row = await S.plan(ctx, { title: H.planTitle(kind, cat, words, card && card.type), date: opts.date, start: opts.start, minutes: opts.minutes, who: opts.who, placeId: id, kind, named: !!(cat && cat.name) });
      const gone = sheet && sheet.replaces ? sheet.replaces : null;
      if (gone) await S.dropPlan(ctx, gone.id).catch(() => toast('Planned. Take ' + nameOfPlan(gone) + ' off in Home.'));
      setPlanned((p) => [...p.filter((x) => !gone || x.id !== gone.id), row].sort((a, b) => (a.on_date < b.on_date ? -1 : 1)));
      setSheet(null);
      if (gone) track('courted_swapped', { kind });
      const d = new Date(opts.date + 'T12:00:00');
      toast('Planned for ' + (opts.date === today ? 'today' : opts.date === H.addDays(today, 1) ? 'tomorrow' : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getDay()])
        + (partner && opts.who !== 'me' ? '. We’ve asked ' + partner.name + '.' : ''));
      track('courted_planned', { kind, who: opts.who, from: from || 'place', catalogue: !!cat });
      window.dispatchEvent(new CustomEvent('dv-plans-changed', { detail: { from: 'courted' } }));
    } catch (e) { toast(noSignal() ? 'You’re offline. Try again with a signal.' : 'Couldn’t plan that. Try again.'); throw e; }
  };

  // how a date went (0048): your verdict on the place, and the date drops off "How was it?"
  const rateIt = async (p, verdict, from) => {
    const id = p.ref.place.id, kind = p.ref.kind || kindOfPlan(p);
    setRate((r) => r.filter((x) => x.id !== p.id));
    const now = new Date().toISOString();
    setRows((r) => [...(r || []).filter((x) => !(x.user_id === me.id && x.place_id === id)), { place_id: id, user_id: me.id, kind, verdict, created_at: now, updated_at: now }]);
    toast(verdict === 'loved' ? 'In your favourites.' : 'We won’t suggest it again.');
    track('courted_after', { verdict, from });
    try { await S.visit(p.id, verdict); } catch (e) { toast(noSignal() ? 'You’re offline. Try again with a signal.' : 'Couldn’t save that. Try again.'); load(true); }
  };

  // your partner's date (0045): I'm in, or Can't make it (it comes off the week)
  const respond = async (p, answer) => {
    try {
      await S.respond(p.id, answer);
      setPlanned((x) => (answer === 'out' ? x.filter((y) => y.id !== p.id) : x.map((y) => (y.id === p.id ? { ...y, agreed_by: me.id } : y))));
      toast(answer === 'in' ? 'You’re both in. ' + partner.name + ' hears now.' : 'Off the week.');
      track('week_plan_answered', { answer, source: 'courted', from: 'courted' });
      window.dispatchEvent(new CustomEvent('dv-plans-changed', { detail: { from: 'courted' } }));
    } catch (e) { toast(noSignal() ? 'You’re offline. Try again with a signal.' : 'Couldn’t answer that. Try again.'); }
  };
  useEffect(() => {
    if (!ready) return undefined;
    const on = (e) => { if (!e.detail || e.detail.from !== 'courted') S.planned(ctx, today, H.addDays(today, 7)).then(setPlanned).catch(() => {}); };
    window.addEventListener('dv-plans-changed', on);
    return () => window.removeEventListener('dv-plans-changed', on);
  }, [ready, ctx, today]);

  // Find a spot's "Somewhere else": the place's card, your yes (it goes in your ideas, and first in your partner's
  // deck), then Plan it for that day
  const pickElse = async (r) => {
    const got = await S.cards([r.id]).catch(() => ({}));
    if (got && got[r.id]) setCards((c) => ({ ...c, ...got }));
    const card = got && got[r.id];
    const kind = H.kindOf(r.id, catById[r.id], card && !card.missing ? card : null, card && !card.missing ? null : 'outdoors');
    track('courted_spot_else', { action: 'picked', kind });
    if (mineV.v[r.id] !== 'yes') await answer(r.id, kind, 'yes', 'spot');
    openPlan(r.id, loc.spot, 'spot_else');
  };

  /* ---- Perfect days ---- */
  const offline = 'You’re offline. Try again with a signal.';
  const pdBy = (id) => (pdays || []).find((x) => x.id === id) || null;
  const setPd = (id, f) => setPdays((l) => (l || []).map((x) => (x.id === id ? f(x) : x)));
  const busy = useMemo(() => P.busyFrom(busyRows), [busyRows]);
  const townOfDay = (p) => (p && p.town_id ? towns.find((t) => t.id === p.town_id) || null : null);
  // a thing's name: our catalogue's, else Google's shown live, else the words we kept
  const nameOf = useCallback((x) => (x.kind === 'place' ? (catById[x.ref] && catById[x.ref].name) || (cardsById[x.ref] && !cardsById[x.ref].missing && cardsById[x.ref].name) || x.label : x.label), [catById, cardsById]);
  const openDay = (p) => { track('perfect_day_opened', { days: p.days, n: p.items.length }); go('/courted/day/' + p.id); };
  const createDay = async (o) => {
    try {
      const d = await S.newPerfectDay(ctx, o);
      setPdays((l) => [d, ...(l || [])]);
      setSheet(null);
      track('perfect_day_created', { days: o.days, away: !!o.townId });
      go('/courted/day/' + d.id);
    } catch (e) { toast(noSignal() ? offline : 'Couldn’t start it. Try again.'); throw e; }
  };
  const addItem = async (p, day, part, x) => {
    const tmp = { id: 'tmp-' + Date.now(), day_id: p.id, day, part, pos: P.nextPos(p.items, day, part), added_by: me.id, created_at: new Date().toISOString(), ...x };
    setPd(p.id, (d) => ({ ...d, items: [...d.items, tmp] }));
    track('perfect_day_added', { kind: x.kind, part, days: p.days, place_kind: x.place_kind || null });
    try {
      const row = await S.addPerfectItem(ctx, tmp);
      setPd(p.id, (d) => ({ ...d, items: d.items.some((i) => i.id === row.id) ? d.items.filter((i) => i.id !== tmp.id) : d.items.map((i) => (i.id === tmp.id ? row : i)) }));
    } catch (e) {
      setPd(p.id, (d) => ({ ...d, items: d.items.filter((i) => i.id !== tmp.id) }));
      toast(noSignal() ? offline : 'Couldn’t add that. Try again.');
    }
  };
  const moveItem = async (p, x, day, part) => {
    const pos = P.nextPos(p.items.filter((i) => i.id !== x.id), day, part);
    setPd(p.id, (d) => ({ ...d, items: d.items.map((i) => (i.id === x.id ? { ...i, day, part, pos } : i)) }));
    setSheet(null);
    try { await S.movePerfectItem(x.id, { day, part, pos }); } catch (e) { toast(noSignal() ? offline : 'Couldn’t move that. Try again.'); S.perfectDays(ctx).then(setPdays).catch(() => {}); }
  };
  const removeItem = async (p, x) => {
    setPd(p.id, (d) => ({ ...d, items: d.items.filter((i) => i.id !== x.id) }));
    setSheet(null);
    track('perfect_day_removed', { kind: x.kind });
    try { await S.removePerfectItem(x.id); toast('Taken off.'); } catch (e) { toast(noSignal() ? offline : 'Couldn’t take that off. Try again.'); S.perfectDays(ctx).then(setPdays).catch(() => {}); }
  };
  const renameDay = async (p, title) => {
    setPd(p.id, (d) => ({ ...d, title }));
    setSheet(null);
    try { await S.updatePerfectDay(p.id, { title }); toast('Saved.'); } catch (e) { toast(noSignal() ? offline : 'Couldn’t save that. Try again.'); S.perfectDays(ctx).then(setPdays).catch(() => {}); }
  };
  const deleteDay = async (p) => {
    setSheet(null);
    try {
      await S.updatePerfectDay(p.id, { status: 'gone' });
      setPdays((l) => (l || []).filter((x) => x.id !== p.id));
      track('perfect_day_deleted', { n: p.items.length });
      if (router.canBack()) router.back(); else go('/courted/days');
      toast('Deleted.');
    } catch (e) { toast(noSignal() ? offline : 'Couldn’t delete it. Try again.'); }
  };
  // Put it on the week: a plan for each thing on its day and time; a dinner you'd cook (the evening's first recipe)
  // goes on Catered's plan instead, so it's on the shopping list
  const planDay = async (p, { fit, times }) => {
    const hh = (m) => String(Math.floor(m / 60) % 24).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');
    const rows = [], dinners = [], dinnerOn = {};
    for (const x of P.sorted(p.items)) {
      const t = times[x.id] || { day: x.day, start: P.PART_START[x.part], minutes: P.itemMinutes(x) };
      const date = H.addDays(fit.date, t.day);
      const base = { title: x.label, on_date: date, start_time: hh(t.start), minutes: Math.min(600, t.minutes) };
      if (x.kind === 'place') rows.push({ ...base, source: 'courted', ref: { place: { id: x.ref }, kind: x.place_kind || null, named: !!(catById[x.ref] && catById[x.ref].name === x.label) } });
      else if (x.kind === 'title') { const [type, id] = String(x.ref).split(':'); rows.push({ ...base, source: 'curated', ref: { tmdb_id: Number(id), media_type: type } }); }
      else if ((x.kind === 'recipe' || x.kind === 'cook') && x.part === 'evening' && !dinnerOn[date]) { dinnerOn[date] = true; dinners.push({ date, x }); }
      else if (x.kind === 'recipe' || x.kind === 'cook') rows.push({ ...base, source: 'catered', ref: x.kind === 'recipe' ? { recipe: x.ref } : { catalogue: x.ref } });
      else rows.push({ ...base, source: 'intention', ref: {} });
    }
    try { await S.planPerfectDay(ctx, p.id, fit.date, rows); } catch (e) { toast(noSignal() ? offline : 'Couldn’t plan it. Try again.'); throw e; }
    let dinnerLeft = false;
    for (const d of dinners) {
      try {
        const T = await import('../tabs/sync.js');
        const rid = d.x.kind === 'cook' ? await (await import('../games/sync.js')).saveCatalogue(d.x.ref) : d.x.ref;
        await T.addDinner(d.date, rid);
      } catch (e) { dinnerLeft = true; }
    }
    setPd(p.id, (d) => ({ ...d, planned_on: fit.date }));
    setSheet(null);
    const w = p.days === 3 ? P.dateLabel(fit, ctx.country) : P.dayWord(fit.date, today, ctx.country);
    toast(dinnerLeft ? 'On your week. Add the dinner in Catered.' : 'On your week ' + (w === 'today' || w === 'tomorrow' ? w : 'for ' + w) + '.' + (partner ? ' ' + partner.name + ' hears now.' : ''), 4000);
    track('perfect_day_planned', { days: p.days, n: p.items.length, free: !fit.asked, away: !!p.town_id, dinner: dinners.length > 0 });
    window.dispatchEvent(new CustomEvent('dv-plans-changed', { detail: { from: 'courted' } }));
    S.plansBetween(ctx, today, H.addDays(today, 60)).then(setBusyRows).catch(() => {});
  };
  // an address that opens a sheet: ?new=1 on the list, ?plan= on a perfect day (Home's card), once each
  const asked = useRef('');
  useEffect(() => {
    if (!ready || !pdays) return;
    const k = (loc.newDay ? 'new' : '') + (loc.day && loc.planFor ? loc.day + loc.planFor : '');
    if (!k || asked.current === k) return;
    asked.current = k;
    if (loc.newDay) setSheet({ kind: 'pdnew' });
    else if (pdBy(loc.day) && pdBy(loc.day).items.length) setSheet({ kind: 'pdplan', dayId: loc.day, date: loc.planFor === 'next' ? null : loc.planFor });
  }, [ready, pdays, loc.newDay, loc.day, loc.planFor]);
  // at home, what you'd cook and watch (your recipes, Cook's yeses, Curated's), when you add to a home day
  useEffect(() => {
    if (!sheet || sheet.kind !== 'pdadd' || homePicks) return;
    const p = pdBy(sheet.dayId);
    if (p && !p.town_id) import('../games/sync.js').then((G) => G.picks()).then((x) => setHomePicks(x || { recipes: [], cook: [], titles: [] })).catch(() => setHomePicks({ recipes: [], cook: [], titles: [] }));
  }, [sheet && sheet.kind, sheet && sheet.dayId]);

  /* ---- what shows ---- */
  const narrowedKinds = kinds && kinds.length ? H.DATE_KINDS.filter(([k]) => kinds.includes(k)).map(([, l]) => l.toLowerCase()) : [];
  const narrowed = narrowedKinds.length || forSlot ? [narrowedKinds.join(' and '), forSlot ? (forSlot.date === today ? 'today' : forSlot.dow) : ''].filter(Boolean).join(' on ') : null;
  const pickWhen = (date) => { setForDate(date); track('courted_swipe_for', { set: !!date, day: date ? (date === today ? 'today' : 'ahead') : null }); };
  const forNote = H.forLine(forSlot, forKinds.left, words, forSlot ? skyFor(forSlot.date) : null);
  // one line over the deck (Ben, 1 Nov): what, where and when; it opens What and where
  const bar = ready ? <WhereBar line={H.whereLine({ kinds, town, homeName: ctx.town, slot: forSlot, today })} note={forNote} away={!!town} onOpen={() => setSheet({ kind: 'where' })} /> : null;
  const chips = <ShellChips product="courted" seg label="Courted" items={SECTIONS} active={loc.section} onSelect={(id) => go('/courted/' + id)} />;
  const openSearch = () => go('/courted/search?from=' + loc.section);
  // "best restaurants in Toronto", searched near home: look in Toronto (Google's first town by that name)
  const lookIn = async (name) => {
    track('courted_search_town', {});
    const found = await S.townSearch(ctx, name, null).catch(() => []);
    if (!found.length) { toast('Couldn’t find ' + name + '. Try What and where.'); return; }
    await addTown(found[0], null).catch(() => {});
  };

  let body;
  if (!ctx) body = <div className="co-main"><p className="co-wait">Finding somewhere to go…</p></div>;
  else if (ctx.status === 'signedout') body = <Message title="Date night, planned together" text="Sign in and Courted finds places near home that you’d both like, for when you’re both free." action={<><a className="dv-btn dv-btn-pop" href={SIGN_IN()}>Sign in</a><a className="dv-quiet co-notfor" href={inShell() ? '/join/?next=%2Fshell.html%3Fopen%3D%2Fcourted%2F' : '/join/?next=%2Fcourted%2F'}>New here? Make your home</a></>} />;
  else if (ctx.status === 'nocouple') body = <Message title="Make your home first" text="Courted plans dates for the two of you, so it starts with your home. Make it, then invite your partner." action={<a className="dv-btn dv-btn-pop" href={inShell() ? '/join/?next=%2Fshell.html%3Fopen%3D%2Fcourted%2F' : '/join/?next=%2Fcourted%2F'}>Make your home</a>} />;
  else if (ctx.status === 'offline') body = <Message title="You’re offline" text="Courted needs a signal to find places. Your ideas come back with it." />;
  else if (ctx.status !== 'ready') body = <Message title="Couldn’t open Courted" text="It’s there, we just can’t get to it right now." action={<button type="button" className="dv-btn dv-btn-pop" onClick={() => load(true)}>Try again</button>} />;
  else if (loc.section === 'days') {
    body = <DaysR list={pdays} towns={towns} homeName={ctx.town} me={me} partner={partner} today={today} cc={ctx.country} free={free} busy={busy} nameOf={nameOf} title={!inShell()}
      onOpen={openDay} onNew={() => setSheet({ kind: 'pdnew' })} onPlan={(p, date) => setSheet({ kind: 'pdplan', dayId: p.id, date })} />;
  }
  else if (!ctx.home) body = <Message title="Where’s home?" text={inShell() ? 'Courted finds places near home. Add your town to your usual week, and the deck starts there.' : 'Courted finds places near home. Add your town to your usual week in the Domovoi app, and the deck starts there.'}
    action={inShell() ? <button type="button" className="dv-btn dv-btn-pop" onClick={() => { track('courted_home_asked', {}); sendIntent('week', { kind: 'setup' }); openPath('/shell.html'); }}>Add your town</button> : null} />;
  else if (!rows) body = <div className="co-main"><p className="co-wait">Finding somewhere to go…</p></div>;
  else if (!started) body = <Start ctx={ctx} onStart={start} />;
  else if (loc.section === 'swipe') {
    body = <SwipeR deck={deck} cardsById={cardsById} catById={catById} loading={!deckIds && !deckErr} error={deckErr} partnerName={partner && partner.name} words={words}
      canUndo={!!last} narrowed={narrowed} onUndo={undo} onRetry={() => { S.forgetDeck(); loadDeck(); }} bar={bar}
      onDecide={(item, v) => answer(item.id, H.kindOf(item.id, catById[item.id], cardsById[item.id], item.kind), v, 'deck')}
      onOpen={(id) => go('/courted/place/' + encodeURIComponent(id) + '?from=swipe')} onFilters={() => { if (narrowed) { setKindsKept([]); setForDate(null); } else setSheet({ kind: 'where' }); }} />;
  } else if (loc.section === 'ideas') {
    body = <IdeasR ideas={ideas} waiting={waiting} mine={mine} favs={favs} notAgain={notAgain} away={away} cardsById={cardsById} catById={catById} partnerName={partner && partner.name} solo={!partner}
      onOpen={(id) => go('/courted/place/' + encodeURIComponent(id) + '?from=ideas')} onSwipe={() => go('/courted/swipe')} />;
  } else {
    body = <WeekR web={!inShell()} slots={slots} picks={picks} planned={planned} cardsById={cardsById} catById={catById} today={today} partner={partner} weekKnown={!!free} sunsetFor={sunsetFor} words={words}
      theatres={theatres} onFilm={(id) => { track('courted_film_opened', { both: !!(theatres && theatres.both.some((f) => f.id === id)) }); openPath('/curated/?t=movie-' + id); }}
      onSwipeOn={() => { track('courted_swipe_whats_on', {}); sendIntent('curated', { type: 'now_playing' }); openPath('/curated/?tab=deck'); }}
      onPlan={(id, date) => openPlan(id, date, 'week')} onOpen={(id, date) => go('/courted/place/' + encodeURIComponent(id) + '?from=week' + (typeof date === 'string' ? '&for=' + date : ''))} onSwipe={(date) => go('/courted/swipe' + (typeof date === 'string' ? '?for=' + date : ''))}
      wx={wx} chance={chance} country={ctx.country} kindOfPlan={kindOfPlan} onSpot={openSpot}
      ideas={ideasK} onSwap={openSwap} meId={me.id} onRespond={respond} rate={rate} onRate={(p, v) => rateIt(p, v, 'week')}
      searchLabel={town ? 'Search ' + town.name : 'Search places near ' + (ctx.town || 'home')} onSearch={openSearch} />;
  }

  // a perfect day, over the list (a place you open from it goes over it)
  let dpage = null;
  const pd = ready && loc.day && pdays ? pdBy(loc.day) : null;
  if (pd) {
    dpage = <DayPage p={pd} towns={towns} homeName={ctx.town} me={me} partner={partner} cardsById={cardsById} catById={catById} nameOf={nameOf}
      onBack={() => { if (router.canBack()) router.back(); else go('/courted/days'); }}
      onAdd={(day, part) => setSheet({ kind: 'pdadd', dayId: pd.id, day, part })} onItem={(x) => setSheet({ kind: 'pditem', dayId: pd.id, itemId: x.id })}
      onPlan={() => setSheet({ kind: 'pdplan', dayId: pd.id, date: null })} onMore={() => setSheet({ kind: 'pdmore', dayId: pd.id })} />;
  }
  // a place, over the section
  let page = null;
  if (ready && rows && loc.place) {
    const id = loc.place, card = cardsById[id], cat = catById[id];
    const kind = H.kindOf(id, cat, card, mineV.k[id] || theirsV.k[id]);
    // the day you came for, else the first free time that fits
    const slot = H.placeSlot(planSlots, kind, { want: loc.placeFor || forDate, sunsetFor, skyFor });
    page = <PlaceR id={id} card={card} cat={cat} mine={mineV.v[id]} theirs={theirsV.v[id]} partnerName={partner && partner.name} solo={!partner} slot={slot} sunset={slot ? sunsetFor(slot.date) : null} wx={slot ? wxNote(slot.date) : null}
      words={words} today={today} onBack={back} backLabel={loc.via === 'search' ? 'Search' : loc.via === 'day' ? 'Back' : secLabel(loc.section)}
      onPlan={() => openPlan(id, slot && slot.date, 'place')} onAnswer={(v) => { answer(id, kind, v, 'place'); if (v === 'no') back(); }} />;
  }
  // the morning after a date (the push): How was it?
  if (ready && rows && loc.how && !sheet) {
    const p = rate.find((x) => x.id === loc.how);
    if (p) {
      const pid = p.ref.place.id;
      page = <>{page}<HowSheet plan={p} name={(catById[pid] && catById[pid].name) || (cardsById[pid] && cardsById[pid].name) || null} card={cardsById[pid]} cat={catById[pid]} kind={kindOfPlan(p)} today={today}
        onAnswer={(v) => { rateIt(p, v, 'push'); go('/courted/week'); }} onClose={() => go('/courted/week')} /></>;
    }
  }
  else if (ready && rows && loc.search != null && !loc.place) {
    page = <SearchR q0={loc.search} town={town} homeName={ctx.town} mine={mineV.v} theirs={theirsV.v} partnerName={partner && partner.name} words={words}
      onBack={back} backLabel={secLabel(loc.section)} onWhere={() => setSheet({ kind: 'where' })}
      onOpen={(id) => go('/courted/place/' + encodeURIComponent(id) + '?from=' + loc.section + '&via=search')}
      onAnswer={(id, kind, v) => answer(id, kind, v, 'search')} onLookIn={lookIn} />;
  }
  else if (ready && rows && loc.spot) {
    // the deck's outdoor places, and your partner's outdoor yeses waiting for yours
    const theirYes = {};
    waiting.forEach((id) => { if (theirsV.k[id] === 'outdoors') theirYes[id] = true; });
    const outdoorsIds = [...new Set([...Object.keys(theirYes), ...((deckIds && deckIds.outdoors) || [])])].filter((id) => !mineV.v[id]);
    page = <SpotR date={loc.spot} today={today} wx={wx} chance={chance} ideas={ideasK} nearby={outdoorsIds} theirYes={theirYes}
      cardsById={cardsById} catById={catById} partner={partner} words={words} country={ctx.country} from={loc.from} onBack={back} backLabel="This week"
      onOpen={(id) => go('/courted/place/' + encodeURIComponent(id) + '?from=week')} onPlan={(id) => openPlan(id, loc.spot, 'spot')} onSwipe={() => go('/courted/swipe?for=' + loc.spot)} onNeed={fetchCards}
      search={(q, session) => S.search(ctx, q, session)} onElse={pickElse} />;
  }
  let sh = null;
  if (sheet && sheet.kind === 'where') {
    sh = <WhatWhereSheet Sheet={Sheet} homeName={ctx.town} range={ctx.range} towns={towns} town={town} townLines={townLines} kinds={kinds} left={forKinds.left}
      slots={slots} slot={forSlot} weekKnown={!!free} note={forNote} onWhere={(t) => pickWhere(t)} onRange={setRange} onKinds={setKindsKept} onWhen={pickWhen}
      search={(q, session) => S.townSearch(ctx, q, session)} onAddTown={addTown} onClose={() => setSheet(null)} />;
  } else if (sheet && sheet.kind === 'pdnew' && ready) {
    sh = <NewDaySheet towns={towns} homeName={ctx.town} town0={town} partner={partner} search={(q, session) => S.townSearch(ctx, q, session)} onAddTown={addTown}
      onCreate={createDay} onClose={() => setSheet(null)} />;
  } else if (sheet && sheet.kind && sheet.kind.startsWith('pd') && ready && pdBy(sheet.dayId)) {
    const p = pdBy(sheet.dayId), t = townOfDay(p);
    if (sheet.kind === 'pdadd') {
      sh = <AddSheet p={p} day={sheet.day} part={sheet.part} town={t} homeName={ctx.town} rows={rows || []} me={me} partner={partner} cardsById={cardsById} catById={catById} words={words} nameOf={nameOf}
        homePicks={homePicks} onNeed={(ids, tw) => fetchCards(ids, tw)} onAdd={(x) => addItem(p, sheet.day, sheet.part, x)}
        onSwipe={() => { setSheet(null); pickWhere(t, 'perfect'); go('/courted/swipe'); }} onClose={() => setSheet(null)} />;
    } else if (sheet.kind === 'pditem') {
      const x = p.items.find((i) => i.id === sheet.itemId);
      if (x) sh = <ItemSheet p={p} item={x} name={nameOf(x)} onOpenPlace={() => { setSheet(null); go('/courted/place/' + encodeURIComponent(x.ref) + '?from=days&via=day'); }}
        onMove={(day, part) => moveItem(p, x, day, part)} onRemove={() => removeItem(p, x)} onClose={() => setSheet(null)} />;
    } else if (sheet.kind === 'pdmore') {
      sh = <MoreSheet p={p} onRename={(title) => renameDay(p, title)} onDelete={() => deleteDay(p)} onClose={() => setSheet(null)} />;
    } else if (sheet.kind === 'pdplan') {
      const fits = P.fitDates(p, p.items, { free, busy, today, n: 3 });
      const km = t && ctx.home ? H.kmBetween(ctx.home, { lat: Number(t.lat), lng: Number(t.lng) }) : null;
      sh = <PlanDaySheet p={p} fits={fits} date0={sheet.date} today={today} cc={ctx.country} partner={partner} kmAway={km} townName={t ? t.name : null} nameOf={nameOf}
        onPlan={(o) => planDay(p, o)} onClose={() => setSheet(null)} />;
    }
  } else if (sheet && sheet.kind === 'plan') {
    const id = sheet.id, card = cardsById[id], cat = catById[id];
    const kind = H.kindOf(id, cat, card, mineV.k[id]);
    sh = <PlanSheet name={(cat && cat.name) || (card && card.name) || 'it'} kind={kind} cat={cat} partner={partner} me={me} slots={planSlots} today={today} sunsetFor={sunsetFor} words={words} wxFor={wxNote}
      initialDate={sheet.date} initialStart={sheet.replaces ? hm(sheet.replaces.start_time) : null} instead={sheet.replaces ? nameOfPlan(sheet.replaces) : null}
      onPlan={(o) => doPlan(id, o, sheet.from)} onClose={() => setSheet(null)} />;
  }
  const af = after && ready && !match ? <After a={after} card={cardsById[after.id]} cat={catById[after.id]} kind={after.kind} onClose={() => setAfter(null)}
    onAnswer={(v) => answer(after.id, after.kind, v, 'deck_been')} /> : null;
  const m = match && ready ? <Match m={match} card={cardsById[match.id]} cat={catById[match.id]} kind={match.kind} onClose={() => setMatch(null)} onPlan={() => { setMatch(null); openPlan(match.id, match.date || null, match.mine ? 'swipe_for' : 'match'); }} /> : null;
  return (
    <div className="co-app">
      {ready && started ? chips : null}
      {body}
      {dpage}
      {page}
      {sh}
      {m}
      {af}
      {toastEl}
    </div>
  );
}

export function mount(el) { ReactDOM.createRoot(el).render(<App />); }
